/**
 * DECOMURA: Moja Kolekcja — JS
 * Obsługa: produkty, nazwane kolekcje, share modal, info box
 * Dane: localStorage (decomura_collections / decomura_active_collection)
 * Kompatybilność wsteczna: migracja ze starego formatu decomura_collection
 */
(function () {
  'use strict';

  // Wspólny adapter obu stron. Gdy localStorage jest zablokowany albo pełny,
  // zapis zostaje w tej karcie (sessionStorage), a ostatecznie w pamięci strony.
  const storage = window.__dcmCollectionStorage || (window.__dcmCollectionStorage = (() => {
    const memory = new Map();
    const prefix = 'dcm-storage-fallback:';
    return {
      getItem(key) {
        if (memory.has(key)) return memory.get(key);
        try {
          const temporary = window.sessionStorage.getItem(prefix + key);
          if (temporary !== null) return JSON.parse(temporary);
        } catch (_) {}
        try { return window.localStorage.getItem(key); } catch (_) { return null; }
      },
      setItem(key, value) {
        try {
          if (value === null) window.localStorage.removeItem(key);
          else window.localStorage.setItem(key, String(value));
          memory.delete(key);
          try { window.sessionStorage.removeItem(prefix + key); } catch (_) {}
          return;
        } catch (_) {}
        memory.set(key, value === null ? null : String(value));
        try { window.sessionStorage.setItem(prefix + key, JSON.stringify(value === null ? null : String(value))); } catch (_) {}
      },
      removeItem(key) { this.setItem(key, null); }
    };
  })());


  /* =========================================================
     CONFIG & CONSTANTS
     ========================================================= */
  const HANDLE_RE = /^[a-z0-9][a-z0-9_-]*$/;
  const MAX_HANDLES = 90;
  const LEGACY_MAX_HANDLES_URL = 50;
  const MAX_COLLECTIONS = 10;
  const SHARE_API = 'https://decomura-erp.vercel.app/api/collection/share';
  const STORAGE_KEY = 'decomura_collections';
  const ACTIVE_KEY = 'decomura_active_collection';
  const OLD_KEY = 'decomura_collection';

  /* =========================================================
     I18N — loaded from JSON block in Liquid
     ========================================================= */
  let i18n = {};
  try {
    const el = document.getElementById('mk-i18n');
    if (el) i18n = JSON.parse(el.textContent);
  } catch (_) {}

  // Teksty pochodzą z sześciu plików locales, przekazanych przez Liquid.
  let dcmTexts = {};
  try { dcmTexts = JSON.parse(document.getElementById('dcm-staging-i18n').textContent); } catch (_) {}
  function dcmText(key, values) {
    let value = dcmTexts[key] || '';
    Object.keys(values || {}).forEach(name => { value = value.replaceAll('[' + name + ']', String(values[name])); });
    return value;
  }

  const t = (key) => i18n[key] || dcmText(key);

  /* =========================================================
     DOM refs (lazy — resolved on init)
     ========================================================= */
  const $ = (id) => document.getElementById(id);

  /* =========================================================
     COLLECTIONS DATA LAYER (localStorage)
     ========================================================= */
  function getCollections() {
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
        Object.keys(data).forEach(id => {
          if (!data[id] || !Array.isArray(data[id].handles)) delete data[id];
          else data[id].handles = [...new Set(data[id].handles.filter(h => typeof h === 'string' && HANDLE_RE.test(h)))];
        });
        if (!data._default) data._default = { name: t('collections_default_name'), handles: [] };
        return data;
      }
    } catch (_) {}
    return null;
  }

  function setCollections(data) {
    storage.setItem(STORAGE_KEY, JSON.stringify(data));
    notifyCollectionChange();
  }

  let notifyPending = false;
  function notifyCollectionChange() {
    if (notifyPending) return;
    notifyPending = true;
    queueMicrotask(() => {
      notifyPending = false;
      document.dispatchEvent(new CustomEvent('decomura:collection-changed', { detail: { source: 'moja-kolekcja' } }));
    });
  }

  function getActiveId() {
    const id = storage.getItem(ACTIVE_KEY) || '_default';
    if (getCollections()?.[id]) return id;
    return '_default';
  }

  function setActiveId(id) {
    storage.setItem(ACTIVE_KEY, id);
    notifyCollectionChange();
  }

  /** Migrate old format → new AND sync old key into _default */
  function migrateIfNeeded() {
    let oldHandles = [];
    try {
      const old = storage.getItem(OLD_KEY);
      if (old) oldHandles = JSON.parse(old).filter(h => typeof h === 'string');
    } catch (_) {}

    const existing = getCollections();
    if (!existing) {
      // First-time migration
      const data = {
        _default: { name: t('collections_default_name'), handles: oldHandles }
      };
      setCollections(data);
      setActiveId('_default');
      return;
    }

    // Already migrated — merge any NEW handles from old key into _default
    if (oldHandles.length > 0 && existing._default) {
      const currentSet = new Set(existing._default.handles || []);
      let changed = false;
      oldHandles.forEach(h => {
        if (!currentSet.has(h)) {
          currentSet.add(h);
          changed = true;
        }
      });
      if (changed) {
        existing._default.handles = [...currentSet];
        setCollections(existing);
      }
    }
  }

  function getActiveCollection() {
    const data = getCollections();
    const id = getActiveId();
    if (data && data[id]) return { id, ...data[id] };
    // fallback to _default
    if (data && data._default) return { id: '_default', ...data._default };
    return { id: '_default', name: t('collections_default_name'), handles: [] };
  }

  function updateActiveHandles(handles) {
    const data = getCollections() || {};
    const id = getActiveId();
    if (!data[id]) data[id] = { name: t('collections_default_name'), handles: [] };
    data[id].handles = handles;
    setCollections(data);
    // also sync old key for backward compat on other pages
    if (id === '_default') {
      storage.setItem(OLD_KEY, JSON.stringify(handles));
    }
  }

  function createCollection(name, opts) {
    const data = getCollections() || {};
    if (Object.keys(data).length >= MAX_COLLECTIONS) return null;
    const id = 'kol_' + Date.now();
    const col = { name: sanitizeName(name), handles: [] };
    if (opts && opts.shared) col.shared = true;
    if (opts && opts.handles) col.handles = opts.handles;
    data[id] = col;
    setCollections(data);
    return id;
  }

  function renameCollection(id, newName) {
    const data = getCollections();
    if (!data || !data[id]) return;
    data[id].name = sanitizeName(newName);
    setCollections(data);
  }

  function deleteCollection(id) {
    if (id === '_default') return;
    const data = getCollections();
    if (!data || !data[id]) return;
    delete data[id];
    setCollections(data);
    if (getActiveId() === id) setActiveId('_default');
  }

  function sanitizeName(name) {
    return String(name).replace(/[<>"'&]/g, '').trim().substring(0, 40) || dcmText('collection_name');
  }

  function sanitizeHandle(h) {
    return String(h).toLowerCase().replace(/[^a-z0-9_-]/g, '').substring(0, 120);
  }

  /* =========================================================
     PRODUCT FETCH
     ========================================================= */
  const productCache = {};

  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.json`, { signal: controller.signal });
      if (!res.ok) return null;
      const json = await res.json();
      productCache[handle] = json.product;
      return json.product;
    } catch (_) {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function mapWithConcurrency(items, concurrency, mapper) {
    const results = new Array(items.length);
    let cursor = 0;

    async function worker() {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await mapper(items[index]);
      }
    }

    await Promise.all(Array.from(
      { length: Math.min(concurrency, items.length) },
      () => worker()
    ));
    return results;
  }

  /* =========================================================
     CARD RENDERING
     ========================================================= */
  // Kafel ma 180–300 px szerokości, a /products/<handle>.json podaje oryginał z CDN.
  // Bez parametru szerokości dziewięćdziesiąt kafli to kilkadziesiąt megabajtów.
  function skalujObraz(src, szerokosc) {
    if (!src || src.slice(0, 5) === 'data:') return src;
    try {
      const u = new URL(src, window.location.href);
      if (u.pathname.indexOf('/cdn/') < 0 && u.hostname.indexOf('cdn.shopify.com') < 0) return src;
      u.searchParams.set('width', String(szerokosc));
      return u.toString();
    } catch (_) { return src; }
  }

  /**
   * Pobiera kafel wyrenderowany przez MOTYW (`templates/product.kafel.liquid`), czyli ten sam,
   * który stoi na listingu kolekcji — razem z wizualizacjami, najechaniem, prawdziwym sercem
   * i obrazami do 2200 px.
   *
   * Wcześniej kafel był tutaj sklejany ręcznie: jeden płaski obraz do 900 px, bez klatek
   * i bez przełącznika wielkości. Dwa niezależne kody robiące to samo rozjeżdżały się przy
   * każdej zmianie listingu — stąd ta zmiana.
   */
  async function pobierzKafel(handle) {
    const korzen = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(korzen + 'products/' + encodeURIComponent(handle) + '?view=kafel', {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('kafel ' + res.status);
    const html = await res.text();
    // Pojemnikiem MUSI być `ul`: `<li>` wklejony do `div.innerHTML` zostaje przez parser
    // wyrzucony, bo poza listą jest niedozwolony — i kafel po cichu znika.
    const kosz = document.createElement('ul');
    kosz.innerHTML = html.trim();
    const el = kosz.firstElementChild;
    if (!el) throw new Error('kafel: pusta odpowiedź');
    return el;
  }

  function createCard(product, mode = 'own') {
    const card = document.createElement('div');
    card.className = 'mk-card';
    card.dataset.handle = product.handle;

    const img = product.images && product.images[0]
      ? product.images[0].src
      : 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400"><rect fill="#f5f5f5" width="300" height="400"/></svg>');

    const heartSvg = mode === 'own'
      ? '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
      : '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';

    const heartClass = mode === 'own' ? 'mk-card__heart' : 'mk-card__heart mk-card__heart--add';
    const heartTitle = mode === 'own' ? t('remove') : t('add_all_btn');
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;

    card.innerHTML = `
      <div class="mk-card__image-wrap">
        <a href="${productUrl}">
          <img class="mk-card__image" src="${escapeHtml(skalujObraz(img, 400))}" srcset="${escapeHtml(skalujObraz(img, 300))} 300w, ${escapeHtml(skalujObraz(img, 400))} 400w, ${escapeHtml(skalujObraz(img, 600))} 600w, ${escapeHtml(skalujObraz(img, 900))} 900w" sizes="(min-width: 990px) 300px, 45vw" alt="${escapeHtml(product.title)}" loading="lazy" decoding="async" width="400" height="533">
        </a>
        <button class="${heartClass}" data-handle="${product.handle}" title="${escapeHtml(heartTitle)}" aria-label="${escapeHtml(heartTitle)}" type="button">
          ${heartSvg}
        </button>
      </div>
      <div class="mk-card__info">
        <a href="${productUrl}" class="mk-card__title">${escapeHtml(product.title)}</a>
      </div>`;

    // Event: own = remove; shared = add
    const heartBtn = card.querySelector('button');
    if (mode === 'own') {
      heartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        removeFromCollection(product.handle, card);
      });
    } else {
      heartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        addSharedToCollection(product.handle, heartBtn);
      });
    }

    return card;
  }

  function createSkeleton() {
    const card = document.createElement('div');
    card.className = 'mk-card mk-card--skeleton';
    card.innerHTML = '<div class="mk-card__image-wrap"></div><div class="mk-card__info"></div>';
    return card;
  }

  /* =========================================================
     COLLECTION ACTIONS
     ========================================================= */
  function removeFromCollection(handle, cardEl) {
    const btn = cardEl.querySelector('.mk-card__heart');
    if (btn) btn.classList.add('removing');
    cardEl.classList.add('removing');

    const col = getActiveCollection();
    const newHandles = col.handles.filter(h => h !== handle);
    updateActiveHandles(newHandles);

    setTimeout(() => {
      // Animacja usuwania nie może ukryć kolejnej kolekcji otwartej w międzyczasie.
      const current = getActiveCollection();
      if (current.id !== col.id || !cardEl.isConnected) return;
      cardEl.remove();
      updateCounter(current.handles.length);
      if (current.handles.length === 0) showEmpty();
      renderCollectionsTabs();
    }, 400);
  }

  function addSharedToCollection(handle, btn) {
    const col = getActiveCollection();
    if (col.handles.includes(handle)) return;
    col.handles.push(handle);
    updateActiveHandles(col.handles);
    btn.classList.add('added');
    showToast(t('added_to_collection'));
    updateCounter(col.handles.length);
    renderCollectionsTabs();
  }

  /* =========================================================
     UI HELPERS
     ========================================================= */
  function showToast(msg) {
    const toast = $('mkToast');
    if (!toast) return;
    toast.textContent = msg;
    toast.style.display = 'block';
    requestAnimationFrame(() => {
      toast.classList.add('visible');
      setTimeout(() => {
        toast.classList.remove('visible');
        setTimeout(() => { toast.style.display = 'none'; }, 300);
      }, 2500);
    });
  }

  function updateCounter(count) {
    const counter = $('mkCounter');
    const value = $('mkCounterValue');
    const label = $('mkCounterText');
    if (!counter || !value || !label) return;
    const category = new Intl.PluralRules(document.documentElement.lang || 'pl').select(count);
    value.textContent = count;
    label.textContent = t('counter_' + category) || t('counter_other');
    counter.style.display = count > 0 ? 'inline-block' : 'none';
  }

  function showEmpty() {
    const grid = $('mkGrid');
    const empty = $('mkEmpty');
    const footer = $('mkFooter');
    if (grid) grid.style.display = 'none';
    if (empty) empty.style.display = '';
    if (footer) footer.style.display = 'none';
  }

  function escapeHtml(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* =========================================================
     COLLECTIONS TABS UI
     ========================================================= */
  function renderCollectionsTabs() {
    const bar = $('mkCollectionsBar');
    const tabs = $('mkCollectionsTabs');
    if (!bar || !tabs) return;

    const data = getCollections();
    if (!data) return;

    const keys = Object.keys(data);
    // Always show bar so user can create new collections via +
    bar.style.display = 'flex';

    const activeId = getActiveId();
    tabs.innerHTML = '';

    keys.forEach(id => {
      const col = data[id];
      // Wybór, zmiana nazwy i usunięcie mają oddzielne przyciski, bez zagnieżdżania.
      const tab = document.createElement('div');
      tab.className = 'mk-collection-tab' + (id === activeId ? ' active' : '') + (col.shared ? ' mk-collection-tab--shared' : '');

      const sharedIcon = col.shared ? '<span class="mk-collection-tab__shared-icon" title="'+escapeHtml(dcmText('received_collection'))+'">📩</span>' : '';

      let actionsHtml = '';
      const renameHtml = `<button class="mk-collection-tab__action mk-action-rename" type="button" title="${escapeHtml(t('collections_rename'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>`;
      const deleteHtml = id !== '_default' ? `<button class="mk-collection-tab__action mk-action-delete" type="button" title="${escapeHtml(dcmText('delete_collection'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>` : '';
      actionsHtml = `<span class="mk-collection-tab__actions">${renameHtml}${deleteHtml}</span>`;

      tab.innerHTML = `<button class="mk-collection-tab__select" type="button" aria-pressed="${id === activeId}">${sharedIcon}<span class="mk-collection-tab__name">${escapeHtml(col.name)}</span>
        <span class="mk-collection-tab__count">${col.handles.length}</span></button>
        ${actionsHtml}`;

      // Switch collection
      tab.addEventListener('click', (e) => {
        if (e.target.closest('.mk-collection-tab__action')) return;
        setActiveId(id);
        renderPage();
      });

      // Rename
      const renameBtn = tab.querySelector('.mk-action-rename');
      if (renameBtn) {
        renameBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const newName = prompt(t('collections_name_prompt'), col.name);
          if (newName && newName.trim()) {
            renameCollection(id, newName.trim());
            renderPage();
          }
        });
      }

      // Delete
      const deleteBtn = tab.querySelector('.mk-action-delete');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const msg = t('collections_delete_confirm').replace('{name}', col.name);
          if (confirm(msg)) {
            deleteCollection(id);
            renderPage();
          }
        });
      }

      tabs.appendChild(tab);
    });
  }

  /* =========================================================
     SHARE MODAL
     ========================================================= */
  function getLegacyShareUrl(col) {
    // Bez nazwy kolekcji w adresie: klienci nazywają je nazwiskiem i adresem montażu,
    // a taki adres trafia do pomiaru odbiorcy i do serwisu, w którym odsyłacz zostaje wklejony.
    const handles = col.handles.slice(0, LEGACY_MAX_HANDLES_URL).join(',');
    return window.location.origin + window.location.pathname + '?shared=' + encodeURIComponent(handles);
  }

  async function createShareUrl(col) {
    if (!col.handles.length) return window.location.origin + window.location.pathname;
    if (col.handles.length > MAX_HANDLES) throw new Error('share_limit_exceeded');
    const res = await fetch(SHARE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: col.name, handles: col.handles.slice(0, MAX_HANDLES) }),
    });
    const data = await res.json();
    if (!res.ok || !data.shareId || !/^[A-Za-z0-9_-]{16,32}$/.test(data.shareId)) {
      throw new Error('share_link_failed');
    }
    return window.location.origin + window.location.pathname + '?share=' + encodeURIComponent(data.shareId);
  }

  function setShareControlsEnabled(enabled) {
    const copyBtn = $('mkShareLinkCopy');
    if (copyBtn) copyBtn.disabled = !enabled;
    ['mkShareWhatsApp', 'mkShareMessenger', 'mkShareFacebook', 'mkShareEmail', 'mkSharePinterest'].forEach(id => {
      const option = $(id);
      if (!option) return;
      option.style.pointerEvents = enabled ? '' : 'none';
      option.style.opacity = enabled ? '' : '0.45';
      option.setAttribute('aria-disabled', String(!enabled));
      // Także Enter nie może otworzyć poprzedniego linku, gdy czekamy na nowy.
      if (!enabled) option.removeAttribute('href');
    });
  }

  function configureShareLinks(url) {
    const input = $('mkShareLinkInput');
    if (input) input.value = url;

    const wa = $('mkShareWhatsApp');
    if (wa) wa.href = `https://wa.me/?text=${encodeURIComponent(url)}`;

    const msg = $('mkShareMessenger');
    if (msg) msg.href = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=0&redirect_uri=${encodeURIComponent(url)}`;

    const fb = $('mkShareFacebook');
    if (fb) fb.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

    const em = $('mkShareEmail');
    if (em) em.href = `mailto:?subject=${encodeURIComponent(t('share_email_subject'))}&body=${encodeURIComponent(t('share_email_body') + '\n\n' + url)}`;

    const pin = $('mkSharePinterest');
    if (pin) pin.href = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}`;

    const copyBtn = $('mkShareLinkCopy');
    const copyLabel = $('mkCopyLabel');
    if (copyBtn) {
      copyBtn.onclick = () => {
        navigator.clipboard.writeText(url).then(() => {
          if (copyLabel) copyLabel.textContent = '✓ ' + t('share_copied');
          setTimeout(() => {
            if (copyLabel) copyLabel.textContent = t('share_copy');
          }, 2000);
        });
      };
    }
  }

  let shareRevision = 0;
  let shareCollectionKey = null;
  let shareOpener = null;
  function shareKey(col) { return JSON.stringify([col.id, col.name, col.handles]); }

  async function openShareModal() {
    const modal = $('mkShareModal');
    if (!modal) return;

    const revision = ++shareRevision;
    shareOpener = document.activeElement;
    const col = getActiveCollection();
    shareCollectionKey = shareKey(col);
    function isCurrent() {
      if (revision !== shareRevision) return false;
      if (shareCollectionKey !== shareKey(getActiveCollection())) {
        closeShareModal();
        return false;
      }
      return true;
    }

    const input = $('mkShareLinkInput');
    if (input) input.value = '...';
    setShareControlsEnabled(false);
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    // Klawiatura pozostaje w otwartym oknie udostępniania.
    if (typeof trapFocus === 'function') trapFocus(modal, $('mkShareClose'));
    else $('mkShareClose')?.focus();

    try {
      const url = await createShareUrl(col);
      if (!isCurrent()) return;
      configureShareLinks(url);
      setShareControlsEnabled(true);
    } catch (error) {
      if (!isCurrent()) return;
      if (error && error.message === 'share_limit_exceeded') {
        closeShareModal();
        showToast(dcmText('share_limit', { count: MAX_HANDLES }));
        return;
      }
      if (col.handles.length <= LEGACY_MAX_HANDLES_URL) {
        configureShareLinks(getLegacyShareUrl(col));
        setShareControlsEnabled(true);
        return;
      }
      closeShareModal();
      showToast(dcmText('share_error'));
    }
  }

  function closeShareModal() {
    ++shareRevision;
    shareCollectionKey = null;
    const modal = $('mkShareModal');
    if (!modal) return;
    const wasOpen = modal.style.display === 'flex';
    modal.style.display = 'none';
    setShareControlsEnabled(false);
    document.body.style.overflow = '';
    if (wasOpen) {
      const target = shareOpener?.isConnected ? shareOpener : $('mkShare');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(target);
      else target?.focus();
    }
  }

  /* =========================================================
     INFO BOX TOGGLE
     ========================================================= */
  function initInfoBox() {
    const toggle = $('mkInfoToggle');
    const content = $('mkInfoContent');
    if (!toggle || !content) return;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      content.classList.toggle('open');
    });
  }

  /* =========================================================
     SHARED PRODUCTS (short ?share=id or legacy ?shared=handle1,handle2)
     ========================================================= */
  async function getSharedCollectionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('shared');
    if (raw) {
      return {
        handles: raw.split(',')
          .map(h => sanitizeHandle(h))
          .filter(h => h.length > 2 && HANDLE_RE.test(h))
          .slice(0, MAX_HANDLES),
        name: params.get('name') || null,
      };
    }

    const shareId = params.get('share');
    if (!shareId || !/^[A-Za-z0-9_-]{16,32}$/.test(shareId)) return null;

    try {
      const res = await fetch(SHARE_API + '?id=' + encodeURIComponent(shareId));
      const data = await res.json();
      if (!res.ok || !Array.isArray(data.handles)) throw new Error('share_read_failed');
      return {
        handles: data.handles
          .map(h => sanitizeHandle(h))
          .filter(h => h.length > 2 && HANDLE_RE.test(h))
          .slice(0, MAX_HANDLES),
        name: data.name || null,
      };
    } catch (_) {
      showToast(dcmText('import_error'));
      return null;
    }
  }

  function czyscAdresUdostepnienia() {
    const cleanUrl = new URL(window.location.href);
    ['share', 'shared', 'name'].forEach(key => cleanUrl.searchParams.delete(key));
    const state = Object.assign({}, window.history.state || {}, { searchParams: cleanUrl.search.slice(1) });
    window.history.replaceState(state, '', cleanUrl);
  }

  async function importSharedCollection(sharedCollection) {
    const sharedHandles = [...new Set(sharedCollection.handles.filter(h => typeof h === 'string' && HANDLE_RE.test(h)))];
    if (!sharedHandles.length) return;

    migrateIfNeeded();

    // Show loading
    const loading = $('mkLoading');
    if (loading) loading.style.display = '';

    // Determine collection name from shared payload or use default
    const sharedName = sharedCollection.name;
    const collectionName = sharedName ? sanitizeName(sharedName) : dcmText('received_collection');

    // Ten sam odsyłacz otwarty drugi raz nie ma tworzyć kolejnej kopii: limit kolekcji jest
    // twardy i po dziesięciu wejściach klient nie mógł już założyć własnej.
    const wszystkie = getCollections() || {};
    const klucz = sharedHandles.slice().sort().join(',');
    const taSamaKolekcja = Object.keys(wszystkie).find(id => {
      const kol = wszystkie[id];
      return kol && kol.shared && (kol.handles || []).slice().sort().join(',') === klucz;
    });
    if (taSamaKolekcja) {
      setActiveId(taSamaKolekcja);
      showToast(dcmText('import_received', { name: wszystkie[taSamaKolekcja].name || collectionName, count: sharedHandles.length }));
      czyscAdresUdostepnienia();
      if (loading) loading.style.display = 'none';
      renderPage();
      return;
    }
    // Zapis do pamięci przeglądarki wymaga zgody klienta — cudzy odsyłacz nią nie jest.
    if (!confirm(dcmText('import_confirm', { name: collectionName, count: sharedHandles.length }))) {
      czyscAdresUdostepnienia();
      if (loading) loading.style.display = 'none';
      renderPage();
      return;
    }

    // Dostępność produktu w tej chwili lub na tym rynku nie określa zawartości
    // otrzymanej kolekcji. Zachowujemy cały zestaw; renderPage pobiera jego podgląd.
    const newId = createCollection(collectionName, { shared: true, handles: sharedHandles });
    if (!newId) {
      if (loading) loading.style.display = 'none';
      showToast(dcmText('collection_limit', { count: MAX_COLLECTIONS }));
      renderPage();
      return;
    }

    // Switch to the new collection
    setActiveId(newId);
    showToast(dcmText('import_received', { name: collectionName, count: sharedHandles.length }));

    czyscAdresUdostepnienia();

    if (loading) loading.style.display = 'none';
    renderPage();
  }

  /* =========================================================
     MAIN RENDER
     ========================================================= */
  let renderRevision = 0;
  function previewNotice(show) {
    let notice = $('mkPreviewNotice');
    const grid = $('mkGrid');
    if (!notice && grid) {
      notice = document.createElement('div');
      notice.id = 'mkPreviewNotice';
      notice.innerHTML = '<p role="status"></p><button type="button" class="button"></button>';
      notice.querySelector('p').textContent = dcmText('preview_unavailable');
      const retry = notice.querySelector('button');
      retry.textContent = dcmText('retry');
      retry.addEventListener('click', renderPage);
      grid.before(notice);
    }
    if (notice) notice.hidden = !show;
  }
  async function renderPage() {
    const revision = ++renderRevision;
    const loading = $('mkLoading');
    const grid = $('mkGrid');
    const empty = $('mkEmpty');
    const footer = $('mkFooter');

    if (loading) loading.style.display = '';
    if (grid) { grid.style.display = 'none'; grid.innerHTML = ''; }
    if (empty) empty.style.display = 'none';
    if (footer) footer.style.display = 'none';
    previewNotice(false);

    migrateIfNeeded();
    renderCollectionsTabs();

    const col = getActiveCollection();
    if (shareCollectionKey && shareCollectionKey !== shareKey(col)) closeShareModal();
    const handles = col.handles;

    if (!handles.length) {
      if (loading) loading.style.display = 'none';
      showEmpty();
      updateCounter(0);
      return;
    }

    // Show skeletons during load
    if (grid) {
      // Pusty `display` ODDAJE sterowanie klasie `grid` z Dawn, ktora jest `flex`, nie `grid`.
      // Wpisanie tu 'grid' rozsypywalo uklad kafli — zmierzone na zywym listingu.
      grid.style.display = '';
      handles.forEach(() => grid.appendChild(createSkeleton()));
    }

    // Kafle bierzemy z motywu. Gdyby to zawiodło (brak szablonu, błąd sieci), schodzimy do
    // starego, prostego kafla — lepiej pokazać uboższy wzór niż pustą kolekcję.
    const results = await mapWithConcurrency(handles, 8, async handle => {
      try {
        return { status: 'fulfilled', handle, el: await pobierzKafel(handle) };
      } catch (_) {
        try {
          const product = await fetchProduct(handle);
          return product
            ? { status: 'fulfilled', handle: product.handle, el: createCard(product, 'own'), zapasowy: true }
            : { status: 'rejected', handle };
        } catch (reason) {
          return { status: 'rejected', handle, reason };
        }
      }
    });

    // Stary odczyt produktów nie może nadpisać widoku po przełączeniu kolekcji.
    if (revision !== renderRevision || col.id !== getActiveId()) return;
    if (grid) grid.innerHTML = '';
    let validHandles = [];

    results.forEach(r => {
      if (r.status === 'fulfilled' && r.el) {
        r.el.dataset.mkHandle = r.handle;
        if (grid) grid.appendChild(r.el);
        validHandles.push(r.handle);
      }
    });
    // Serc nie trzeba tu nawadniać ręcznie: `collection-heart.js` ma obserwatora na
    // `#MainContent` z `subtree: true`, który reaguje na dorzucone `.grid__item` — czyli
    // dokładnie na te kafle. Sprawdzone w kodzie, nie założone.

    // Błąd sieci, rynku lub chwilowa niedostępność produktu nie usuwa zapisanych wzorów.

    if (loading) loading.style.display = 'none';

    // Licznik opisuje zapisany projekt, także przy niedostępnych podglądach.
    previewNotice(validHandles.length < handles.length);
    if (grid) grid.style.display = validHandles.length ? '' : 'none';
    if (footer) footer.style.display = 'flex';
    updateCounter(handles.length);
  }

  /* =========================================================
     INIT
     ========================================================= */
  async function init() {
    // Kafel z motywu ma PRAWDZIWE serce (to samo co na listingu), a nie własny przycisk
    // „usuń". Gdy ktoś je odhaczy, wzór wypada z kolekcji — widok musi za tym nadążyć.
    // `collection-heart.js` zgłasza to zdarzenie przy każdej zmianie zapisanych wzorów.
    // Nie przeładowujemy całej strony: każdy kafel to osobne pobranie z motywu, więc pełne
    // odświeżenie przy każdym kliknięciu serca kosztowałoby kilkadziesiąt żądań. Wystarczy
    // zdjąć te kafle, których nie ma już w zapisanych — dokładanie na tej stronie nie zachodzi.
    document.addEventListener('decomura:collection-changed', () => {
      const grid = $('mkGrid');
      if (!grid) return;
      const zapisane = new Set(getActiveCollection().handles);
      let zostalo = 0;
      grid.querySelectorAll('[data-mk-handle]').forEach((kafel) => {
        if (zapisane.has(kafel.dataset.mkHandle)) zostalo += 1;
        else kafel.remove();
      });
      updateCounter(zapisane.size);
      if (!zostalo) renderPage();
    });

    // Info box
    initInfoBox();

    // Collections: add new
    const addBtn = $('mkCollectionsAdd');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const name = prompt(t('collections_name_prompt'));
        if (!name || !name.trim()) return;
        const id = createCollection(name.trim());
        if (!id) {
          showToast(dcmText('collection_limit', { count: MAX_COLLECTIONS }));
          return;
        }
        setActiveId(id);
        renderPage();
      });
    }

    // Share button → open modal
    const shareBtn = $('mkShare');
    if (shareBtn) {
      shareBtn.addEventListener('click', openShareModal);
    }

    // Share modal close
    const closeBtn = $('mkShareClose');
    const backdrop = $('mkShareBackdrop');
    if (closeBtn) closeBtn.addEventListener('click', closeShareModal);
    if (backdrop) backdrop.addEventListener('click', closeShareModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeShareModal();
    });

    // Clear all
    const clearBtn = $('mkClearAll');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        // Operacja jest nieodwracalna, a zapisy żyją wyłącznie w tej przeglądarce.
        const ile = (getActiveCollection().handles || []).length;
        if (!ile) return;
        if (!confirm(dcmText('clear_confirm', { count: ile }))) return;
        updateActiveHandles([]);
        renderPage();
      });
    }

    // Shared products from URL — import as new collection
    const sharedCollection = await getSharedCollectionFromUrl();

    if (sharedCollection && sharedCollection.handles.length) {
      // Import shared collection first, then render
      await importSharedCollection(sharedCollection);
    } else {
      // Normal render
      renderPage();
    }
  }

  let refreshTimer;
  function refreshFromOtherPage() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(renderPage, 0);
  }
  window.addEventListener('storage', e => {
    if (!e.key || [STORAGE_KEY, ACTIVE_KEY, OLD_KEY].includes(e.key)) refreshFromOtherPage();
  });
  document.addEventListener('decomura:collection-changed', e => {
    if (e.detail?.source !== 'moja-kolekcja') refreshFromOtherPage();
  });
  window.addEventListener('pageshow', e => { if (e.persisted) refreshFromOtherPage(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshFromOtherPage(); });

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
